// GPS math for the run tracker. Pure functions, no Capacitor here, so they're unit-testable
// off-device — the one part of the native feature we CAN verify without a phone.

const R = 6371000; // Earth radius in metres
const rad = (d) => (d * Math.PI) / 180;

// Great-circle distance between two lat/lng points, in metres (haversine).
export function haversine(a, b) {
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

// Short moving-average over the positions to damp GPS jitter. Each fix wanders a few metres
// around the true spot, and summed raw that wander is counted as distance travelled — the main
// reason a phone over-reads vs a dedicated watch. Averaging neighbouring fixes pulls the track
// back toward the real path. IMPORTANT: only average neighbours that are CLOSE IN TIME, so on a
// sparse background track (fixes tens of metres apart with the screen locked) we don't "cut the
// corners" and under-measure — there, points stay as they are. `t`, `alt`, `accuracy` preserved.
function smoothTrack(points, windowMs = 8000) {
  if (!points || points.length < 3) return points || [];
  return points.map((p, i) => {
    let slat = 0, slng = 0, n = 0;
    for (let j = i - 1; j <= i + 1; j++) {
      if (j < 0 || j >= points.length) continue;
      const q = points[j];
      if (q.t != null && p.t != null && Math.abs(q.t - p.t) > windowMs) continue; // too far apart in time
      slat += q.lat; slng += q.lng; n++;
    }
    return n ? { ...p, lat: slat / n, lng: slng / n } : p;
  });
}

// Prepare a track for measurement: drop only clearly BAD fixes (very loose accuracy), then smooth.
// The accuracy gate is applied ONLY when it keeps most of the track — if it would throw away a
// large share of the fixes, the accuracy readings are unreliable for this run, so we keep them
// all and let smoothing + the speed guard handle the noise. Under-measuring (dropping real
// movement, e.g. reporting 74 m for a 3 km run) is far worse than a slightly long track, so the
// gate must never gut the track.
export function cleanTrack(points, maxAccM = 50) {
  if (!points || points.length < 2) return points || [];
  const good = points.filter(p => p.accuracy == null || p.accuracy <= maxAccM);
  const base = good.length >= points.length * 0.75 ? good : points;
  return smoothTrack(base);
}

// Total distance of a track, summing leg by leg over the cleaned track. A leg is dropped when it
// implies an impossible speed — that's a GPS glitch teleporting you, not real movement. Rejecting
// by SPEED (not raw distance) is what lets sparse background fixes, which can be legitimately far
// apart, still count. Points carry `t` (ms); without timestamps we fall back to a generous
// distance cap so a single glitch can't add kilometres.
export function trackDistance(points, maxSpeedMps = 12, fallbackCapM = 500) {
  const pts = cleanTrack(points);
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    const d = haversine(pts[i - 1], pts[i]);
    const dt = (pts[i].t != null && pts[i - 1].t != null)
      ? (pts[i].t - pts[i - 1].t) / 1000
      : null;
    if (dt != null && dt > 0) {
      if (d / dt <= maxSpeedMps) total += d;   // plausible pace → real movement
    } else if (d <= fallbackCapM) {
      total += d;                               // no time info → cap the damage of a glitch
    }
  }
  return total;
}

// Pace in seconds per km (running) or per 100 m (swimming). Null when it can't be computed.
export function paceSeconds(timeSec, distanceM, swim = false) {
  if (!timeSec || !distanceM || timeSec <= 0 || distanceM <= 0) return null;
  return timeSec / (distanceM / (swim ? 100 : 1000));
}

// Seconds -> "m:ss". Used for both pace and elapsed time.
export function fmtClock(sec) {
  if (sec == null || Number.isNaN(sec)) return '—';
  const s = Math.round(Number(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// Instant GPS speed is jittery, so smooth it with an exponential moving average before showing
// it. Returns the new smoothed value (m/s). alpha closer to 1 reacts faster but is noisier.
export function smoothSpeed(prev, sample, alpha = 0.3) {
  if (sample == null || Number.isNaN(sample)) return prev ?? null;
  if (prev == null) return sample;
  return prev + alpha * (sample - prev);
}

// Elevation gain: sum of sustained positive altitude changes. Phone GPS altitude is extremely
// noisy (±10-30 m per fix), which is why the old ±1 m threshold fabricated absurd gains (e.g.
// 178 m on a flat 3 km run). We first smooth the altitude with an EMA, then only count rises
// once they exceed `minStepM` above the last confirmed low — so oscillating noise no longer adds
// up, while a real climb still registers.
export function elevationGain(points, minStepM = 6) {
  const alts = (points || []).map(p => p.alt).filter(a => a != null && !Number.isNaN(a));
  if (alts.length < 2) return 0;
  let smooth = alts[0];
  let anchor = alts[0];   // last confirmed level we measure the next rise from
  let gain = 0;
  for (let i = 1; i < alts.length; i++) {
    smooth = smooth + 0.1 * (alts[i] - smooth);   // heavy EMA — phone altitude is very noisy
    if (smooth - anchor >= minStepM) { gain += smooth - anchor; anchor = smooth; }
    else if (smooth < anchor) { anchor = smooth; } // drifting down → measure the next rise from here
  }
  return gain;
}

// Per-kilometre splits (or per `unitM`). For each unit crossed, the time and pace for that
// stretch. The instant a km boundary falls mid-leg is interpolated linearly within the leg,
// so a split isn't skewed by where the GPS fixes happened to land.
export function computeSplits(rawPoints, unitM = 1000) {
  const splits = [];
  const points = cleanTrack(rawPoints);   // same cleaned track as trackDistance, so totals agree
  if (!points || points.length < 2) return splits;
  let cum = 0;            // cumulative distance
  let mark = 0;           // distance of the last split boundary
  let markT = points[0].t;
  for (let i = 1; i < points.length; i++) {
    const legStart = cum;
    const d = haversine(points[i - 1], points[i]);
    // Reuse the same impossible-speed glitch guard as trackDistance.
    const dt = (points[i].t != null && points[i - 1].t != null) ? (points[i].t - points[i - 1].t) / 1000 : null;
    if (dt != null && dt > 0 && d / dt > 12) continue;
    cum += d;
    while (cum - mark >= unitM) {
      mark += unitM;
      const frac = d > 0 ? (mark - legStart) / d : 1;                 // where in this leg the mark fell
      const tAtMark = points[i - 1].t + frac * (points[i].t - points[i - 1].t);
      const timeSec = (tAtMark - markT) / 1000;
      splits.push({ index: splits.length + 1, distanceM: unitM, timeSec, paceSec: timeSec / (unitM / 1000), partial: false });
      markT = tAtMark;
    }
  }
  // Trailing partial kilometre, only if it's a meaningful stretch (>50 m).
  const rem = cum - mark;
  if (rem > 50) {
    const timeSec = (points[points.length - 1].t - markT) / 1000;
    splits.push({ index: splits.length + 1, distanceM: Math.round(rem), timeSec, paceSec: timeSec / (rem / 1000), partial: true });
  }
  return splits;
}

// Live speed display: show 0 when no fresh GPS fix has arrived for `staleMs`. The GPS only
// reports a speed on a new fix, so when you stop, the last value would otherwise linger — this
// is what makes the on-screen speed drop to 0 when you actually stand still.
export function displaySpeed(lastFixMs, nowMs, speed, staleMs = 4000) {
  if (lastFixMs == null) return 0;
  if (nowMs - lastFixMs > staleMs) return 0;
  return speed != null && speed > 0 ? speed : 0;
}
